import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import svgPaths from './svg-vm2yd368c4';

const SLIDE_THRESHOLD = 0.85;
const RESET_DELAY_MS = 700;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

interface SlideToStartProps {
  onCompleted: () => void;
  disabled?: boolean;
}

const SlideToStart: React.FC<SlideToStartProps> = ({ onCompleted, disabled = false }) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const maxOffsetRef = useRef(0);
  const startXRef = useRef(0);
  const startOffsetRef = useRef(0);
  const offsetRef = useRef(0);
  const pointerIdRef = useRef<number | null>(null);
  const slidingRef = useRef(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [offset, setOffset] = useState(0);
  const [isSliding, setIsSliding] = useState(false);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  const releasePointerCapture = () => {
    if (pointerIdRef.current !== null) {
      try {
        handleRef.current?.releasePointerCapture(pointerIdRef.current);
      } catch {
        // ignore release failures (e.g. pointer already released)
      }
      pointerIdRef.current = null;
    }
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || !trackRef.current || !handleRef.current) {
      return;
    }

    event.preventDefault();

    const trackRect = trackRef.current.getBoundingClientRect();
    const handleRect = handleRef.current.getBoundingClientRect();

    maxOffsetRef.current = Math.max(0, trackRect.width - handleRect.width - 8);
    startXRef.current = event.clientX;
    startOffsetRef.current = offsetRef.current;
    pointerIdRef.current = event.pointerId;
    slidingRef.current = true;
    setIsSliding(true);

    handleRef.current.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || !slidingRef.current) {
      return;
    }

    const delta = event.clientX - startXRef.current;
    const nextOffset = clamp(startOffsetRef.current + delta, 0, maxOffsetRef.current);
    setOffset(nextOffset);
  };

  const resetHandle = () => {
    setOffset(0);
    offsetRef.current = 0;
  };

  const settleHandle = (nextOffset: number) => {
    setOffset(nextOffset);
    offsetRef.current = nextOffset;
  };

  const handlePointerUp = () => {
    if (!slidingRef.current) {
      return;
    }

    slidingRef.current = false;
    setIsSliding(false);
    releasePointerCapture();

    const progress = maxOffsetRef.current === 0 ? 0 : offsetRef.current / maxOffsetRef.current;

    if (progress >= SLIDE_THRESHOLD) {
      onCompleted();
      settleHandle(maxOffsetRef.current);
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      resetTimeoutRef.current = setTimeout(() => {
        setIsSliding(false);
        resetHandle();
      }, RESET_DELAY_MS);
    } else {
      resetHandle();
    }
  };

  const handlePointerCancel = () => {
    if (!slidingRef.current) {
      return;
    }

    slidingRef.current = false;
    setIsSliding(false);
    releasePointerCapture();
    resetHandle();
  };

  return (
    <div
      ref={trackRef}
      className={`slide-to-start${disabled ? ' slide-to-start--disabled' : ''}`}
      role="button"
      aria-label="Glisser pour commencer"
      tabIndex={0}
      aria-disabled={disabled}
      onClick={() => {
        if (disabled) {
          return;
        }
        if (slidingRef.current) {
          return;
        }
        onCompleted();
      }}
    >
      <div className="slide-to-start__label">Glisse pour commencer</div>
      <div className="slide-to-start__chevron" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" role="presentation">
          <path d={svgPaths.p3d301100} fill="var(--fill-0, #A1A1A1)" />
        </svg>
      </div>
      <div
        ref={handleRef}
        className="slide-to-start__handle"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        style={{
          transform: `translateX(${offset}px)`,
          transition: isSliding ? 'none' : 'transform 180ms ease',
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" role="presentation" aria-hidden="true">
          <path d={svgPaths.p24860100} fill="var(--fill-0, #ffffff)" />
        </svg>
      </div>
    </div>
  );
};

export default SlideToStart;
